import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { BillingInterval } from "sdk/types";
import { TextInput } from "~/components";
import { XClose } from "~/icons";
import { discountFromCoupon } from "~/utils/couponCalculations";
import { DiscountLine } from "./DiscountLine";
import { PromoCodeButton } from "./PromoCodeButton";
import { PromoCodeState, Values } from "./types";

interface Props {
  billingInterval: BillingInterval;
  fullPrice: number;
}

export const PromoCodeField: FC<Props> = ({ billingInterval, fullPrice }) => {
  const { t } = useTranslation();
  const [buttonState, setButtonState] = useState<PromoCodeState>("initial");
  const {
    values: { promoCode, coupon },
    resetForm,
  } = useFormikContext<Values>();

  const setTo = (state: PromoCodeState) => () => setButtonState(state);
  const discountAmount = discountFromCoupon(fullPrice, coupon);

  const handleRemove = () => {
    resetForm({
      values: {
        billingInterval,
        coupon: undefined,
        promoCode: "",
      },
    });
    setTo("initial")();
  };

  return (
    <>
      {buttonState === "initial" && (
        <Box mt={3}>
          <Button
            fontSize="1rem"
            h="unset"
            lineHeight="140%"
            onClick={setTo("pending")}
            p={0}
            variant="th-text-link"
          >
            {t("planPayment.promoCode.add")}
          </Button>
        </Box>
      )}
      {buttonState === "pending" && (
        <TextInput
          borderRadius="5px"
          hideLabel
          inputProps={{
            autoFocus: true,
            variant: "stripe",
            placeholder: t("planPayment.promoCode.placeholder"),
          }}
          inputRightElement={<PromoCodeButton onApplied={setTo("applied")} />}
          minH="unset"
          mt={4}
          name="promoCode"
          type="text"
        />
      )}
      {buttonState === "applied" && (
        <Box mt={1} w="full">
          <DiscountLine
            description={promoCode}
            discountAmount={discountAmount}
          />
          <Flex
            as={Button}
            cursor="default"
            justify="space-between"
            variant="stripe"
            w="full"
          >
            <Flex align="center">
              <Text fontSize="14px" fontWeight={300}>
                {promoCode}
              </Text>
            </Flex>

            <XClose
              cursor="pointer"
              onClick={handleRemove}
              color="th.gray4"
              h={5}
              w={5}
            />
          </Flex>
        </Box>
      )}
    </>
  );
};
